import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Shield } from 'lucide-react';
import CardBasic from '../ui/Card.jsx';
import AdminSidebar from './AdminSidebar.jsx';

// Lazy-load Admin-Komponenten
const AdminDashboard = lazy(() => import('./AdminDashboard.jsx'));
const AllUsersIndex = lazy(() => import('./AllUsersIndex.jsx'));
const LogFilesIndex = lazy(() => import('./LogFilesIndex.jsx'));
const SystemHealthIndex = lazy(() => import('./SystemHealthIndex.jsx'));
const CronMonitorIndex = lazy(() => import('./CronMonitorIndex.jsx'));
const EmailStatsIndex = lazy(() => import('./EmailStatsIndex.jsx'));
const FeatureUsageIndex = lazy(() => import('./FeatureUsageIndex.jsx'));
const ActivityTrendsIndex = lazy(() => import('./ActivityTrendsIndex.jsx'));
const ChangelogIndex = lazy(() => import('./ChangelogIndex.jsx'));
const GithubIssuesIndex = lazy(() => import('./GithubIssuesIndex.jsx'));
const MailboxUsageMonitor = lazy(() => import('./MailboxUsageMonitor.jsx'));
const DocumentationIndex = lazy(() => import('./DocumentationIndex.jsx'));
const NewsIndex = lazy(() => import('./NewsIndex.jsx'));
const BlogIndex = lazy(() => import('./BlogIndex.jsx'));
const WebAnalyticsIndex = lazy(() => import('./WebAnalyticsIndex.jsx'));
const TrialUsersIndex = lazy(() => import('./TrialUsersIndex.jsx'));
const ZipDatabaseIndex = lazy(() => import('./ZipDatabaseIndex.jsx'));

// Sidebar-Sections
const SECTIONS = [
    { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard', group: 'Übersicht' },
    { id: 'allUsers', label: 'Alle User', icon: 'Users', group: 'Übersicht' },
    { id: 'trialUsers', label: 'Trial-Nutzer', icon: 'UserPlus', group: 'Übersicht' },
    { id: 'systemHealth', label: 'System Health', icon: 'Server', group: 'System' },
    { id: 'cronMonitor', label: 'Cron-Jobs', icon: 'Clock', group: 'System' },
    { id: 'logFiles', label: 'Log-Dateien', icon: 'FileText', group: 'System' },
    { id: 'zipDatabase', label: 'PLZ-Datenbank', icon: 'Database', group: 'System' },
    { id: 'emailStats', label: 'E-Mail Statistik', icon: 'Mail', group: 'Monitoring' },
    { id: 'mailboxUsage', label: 'Mailbox Nutzung', icon: 'Inbox', group: 'Monitoring' },
    { id: 'featureUsage', label: 'Feature-Nutzung', icon: 'BarChart3', group: 'Monitoring' },
    { id: 'activityTrends', label: 'Aktivitätstrends', icon: 'TrendingUp', group: 'Monitoring' },
    { id: 'webAnalytics', label: 'WebAnalytics', icon: 'LineChart', group: 'Monitoring' },
    { id: 'changelog', label: 'Changelog', icon: 'GitBranch', group: 'Entwicklung' },
    { id: 'githubIssues', label: 'GitHub Issues', icon: 'Bug', group: 'Entwicklung' },
    { id: 'documentation', label: 'Dokumentation', icon: 'BookOpen', group: 'Inhalte' },
    { id: 'news', label: 'News', icon: 'Newspaper', group: 'Inhalte' },
    { id: 'blog', label: 'Blog', icon: 'PenTool', group: 'Inhalte' },
];

const COMPONENT_MAP = {
    dashboard: AdminDashboard,
    allUsers: AllUsersIndex,
    trialUsers: TrialUsersIndex,
    logFiles: LogFilesIndex,
    systemHealth: SystemHealthIndex,
    cronMonitor: CronMonitorIndex,
    emailStats: EmailStatsIndex,
    featureUsage: FeatureUsageIndex,
    activityTrends: ActivityTrendsIndex,
    changelog: ChangelogIndex,
    githubIssues: GithubIssuesIndex,
    mailboxUsage: MailboxUsageMonitor,
    documentation: DocumentationIndex,
    news: NewsIndex,
    blog: BlogIndex,
    webAnalytics: WebAnalyticsIndex,
    zipDatabase: ZipDatabaseIndex,
};

// Styles
const containerStyle = {
    display: 'flex',
    minHeight: '600px'
};

const sidebarStyle = {
    width: '220px',
    flexShrink: 0,
    borderRight: '1px solid var(--border-light, #e2e8f0)',
    background: 'var(--bg-secondary, #f5f7f9)',
    overflowY: 'auto'
};

const contentStyle = {
    flex: 1,
    padding: '1.5rem',
    overflowY: 'auto'
};

const mobileTabBarStyle = {
    display: 'flex',
    overflowX: 'auto',
    borderBottom: '1px solid var(--border-light, #e2e8f0)',
    background: 'var(--bg-secondary, #f5f7f9)',
    padding: '0 0.5rem',
    gap: '0'
};

const mobileTabStyle = {
    padding: '0.75rem 0.875rem',
    fontSize: '0.8rem',
    fontWeight: '500',
    color: 'var(--text-secondary, #64748b)',
    whiteSpace: 'nowrap',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
};

const mobileTabActiveStyle = {
    ...mobileTabStyle,
    color: 'var(--accent-slate, #5b7c8d)',
    borderBottomColor: 'var(--accent-slate, #5b7c8d)'
};

const Spinner = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
        <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Laden...</span>
        </div>
    </div>
);

const AdminLayout = () => {
    const [activeSection, setActiveSection] = useState('dashboard');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSectionChange = (sectionId) => {
        setActiveSection(sectionId);
    };

    const renderContent = () => {
        const Component = COMPONENT_MAP[activeSection];
        if (!Component) {
            return <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>Sektion nicht gefunden</div>;
        }

        return (
            <Suspense fallback={<Spinner />}>
                <Component />
            </Suspense>
        );
    };

    return (
        <div>
            <div className="mb-4">
                <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                    <div>
                        <h1 className="h2 fw-bold text-dark d-flex align-items-center gap-2 mb-2">
                            <Shield size={32} className="icon-contacts" />
                            Administration
                        </h1>
                        <p className="text-muted mb-0">
                            Systemverwaltung und Monitoring
                        </p>
                    </div>
                </div>
            </div>

            <CardBasic style={{ padding: 0, overflow: 'hidden' }}>
                {/* Mobile Tab Bar */}
                {isMobile && (
                    <div style={mobileTabBarStyle}>
                        {SECTIONS.map(section => (
                            <div
                                key={section.id}
                                style={activeSection === section.id ? mobileTabActiveStyle : mobileTabStyle}
                                onClick={() => handleSectionChange(section.id)}
                            >
                                {section.label}
                            </div>
                        ))}
                    </div>
                )}

                <div style={isMobile ? { padding: '1rem' } : containerStyle}>
                    {/* Desktop Sidebar */}
                    {!isMobile && (
                        <div style={sidebarStyle}>
                            <AdminSidebar
                                sections={SECTIONS}
                                activeSection={activeSection}
                                onSectionChange={handleSectionChange}
                            />
                        </div>
                    )}

                    {/* Content Area */}
                    <div style={isMobile ? {} : contentStyle}>
                        {renderContent()}
                    </div>
                </div>
            </CardBasic>
        </div>
    );
};

export default AdminLayout;
